import { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Fade,
  Grow,
  Zoom,
  alpha,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  LocalShipping as TruckIcon,
  Check as CheckIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  ArrowForward as ArrowIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  GpsFixed as GpsIcon,
  Shield as ShieldIcon,
  Group as GroupIcon,
  Build as BuildIcon,
  Agriculture as AgricultureIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

// Import your truck image
import truckImage from "../assets/images/a.png";

const Trucks = () => {
  const [selectedType, setSelectedType] = useState("all");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Brand colors
  const brandColors = {
    green: '#a6ce39',
    black: '#050606',
    orange: '#ec7723',
    white: '#ffffff',
    lightGray: '#f8f9fa'
  };

  // Truck types
  const truckTypes = [
    { id: "all", label: "All Fleet", count: 11, color: brandColors.orange },
    { id: "34 Hose & Trailer", label: "34 Hose & Trailer", count: 4, color: brandColors.green },
    { id: "Drop Side", label: "Drop Side", count: 2, color: brandColors.orange },
    { id: "Low Bed Trailer", label: "Low Bed", count: 1, color: brandColors.green },
    { id: "Gang Truck", label: "Gang Truck", count: 1, color: brandColors.orange },
    { id: "Loader", label: "Loaders", count: 2, color: brandColors.green },
    { id: "Service", label: "Service", count: 2, color: brandColors.orange },
  ];

  // Truck data
  const transcoTrucks = [
    {
      id: 1,
      name: "34 Hose & Trailer",
      type: "34 Hose & Trailer",
      capacity: "Heavy Load",
      length: "34 ft",
      width: "8.5 ft",
      height: "13.5 ft",
      features: ["C-Tracking System", "GPS Monitoring", "Long Distance Transport", "Secure & Insured"],
      quantity: 4,
      description: "Ideal for bulk transport with secure hose trailer system"
    },
    {
      id: 2,
      name: "8 Ton Drop Side",
      type: "Drop Side",
      capacity: "8 Tons",
      length: "24 ft",
      width: "8 ft",
      height: "8 ft",
      features: ["Easy Loading/Unloading", "Versatile Applications", "Local & Regional", "Reliable Performance"],
      quantity: 1,
      description: "Perfect for construction materials and general cargo"
    },
    {
      id: 3,
      name: "4 Ton Drop Side",
      type: "Drop Side",
      capacity: "4 Tons",
      length: "18 ft",
      width: "7.5 ft",
      height: "7.5 ft",
      features: ["Urban Maneuverable", "Quick Turnaround", "Cost-Effective", "Easy Access"],
      quantity: 1,
      description: "Suitable for smaller loads and urban deliveries"
    },
    {
      id: 4,
      name: "Low Bed Hose & Trailer",
      type: "Low Bed Trailer",
      capacity: "Oversized Load",
      length: "40 ft",
      width: "10 ft",
      height: "Low Profile",
      features: ["Heavy Equipment", "Specialized Handling", "Secure Tie-downs", "Expert Operators"],
      quantity: 1,
      description: "For machinery and oversized equipment transport"
    },
    {
      id: 5,
      name: "4 Ton Gang Truck",
      type: "Gang Truck",
      capacity: "4 Tons",
      length: "20 ft",
      width: "7.5 ft",
      height: "8 ft",
      features: ["Crew Transport", "Equipment Carrier", "Versatile", "Reliable Service"],
      quantity: 1,
      description: "Combines personnel and equipment transport"
    },
    {
      id: 6,
      name: "Cameco 2254 Loader",
      type: "Loader",
      capacity: "Sugar Cane",
      length: "Specialized",
      width: "Specialized",
      height: "Specialized",
      features: ["Agricultural", "High Capacity", "Expert Operation", "Specialized"],
      quantity: 1,
      description: "Specialized loader for agricultural operations"
    },
    {
      id: 7,
      name: "John Deere 4x4 Tractor",
      type: "Loader",
      capacity: "Heavy Duty",
      length: "Tractor Unit",
      width: "Standard",
      height: "Standard",
      features: ["4x4 All Terrain", "Versatile", "Agricultural & Construction", "Reliable"],
      quantity: 1,
      description: "All-terrain tractor for various operations"
    },
    {
      id: 8,
      name: "Hino 300 Service Truck",
      type: "Service",
      capacity: "Service Vehicle",
      length: "Medium",
      width: "Standard",
      height: "Standard",
      features: ["Maintenance Support", "On-site Repairs", "Reliable", "Fully Equipped"],
      quantity: 1,
      description: "Fully equipped service vehicle for support operations"
    },
    {
      id: 9,
      name: "3 Van Truck",
      type: "Service",
      capacity: "Mechanics Transport",
      length: "Medium",
      width: "Standard",
      height: "Standard",
      features: ["Mechanics Transport", "Tools Carrier", "Support Operations", "Reliable"],
      quantity: 1,
      description: "Transport for mechanics and their equipment"
    },
    {
      id: 10,
      name: "Operator Support Vehicles",
      type: "Service",
      capacity: "Support Operations",
      length: "Various",
      width: "Various",
      height: "Various",
      features: ["Operator Transport", "Site Support", "Flexible", "Reliable"],
      quantity: 3,
      description: "Support vehicles for operators and field staff"
    },
    {
      id: 11,
      name: "General Transport Fleet",
      type: "34 Hose & Trailer",
      capacity: "Various Loads",
      length: "34 ft",
      width: "8.5 ft",
      height: "13.5 ft",
      features: ["General Cargo", "Secure Transport", "Timely Delivery", "Professional"],
      quantity: 3,
      description: "Additional hose and trailer units for general transport"
    },
  ];

  const filteredTrucks = selectedType === "all"
    ? transcoTrucks
    : transcoTrucks.filter((truck) => truck.type === selectedType);

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setSelectedType(newType);
    }
  };

  const clearFilters = () => {
    setSelectedType("all");
  };

  const getTruckTypeIcon = (type) => {
    switch(type) {
      case 'Loader': return <AgricultureIcon />;
      case 'Service': return <BuildIcon />;
      case 'Gang Truck': return <GroupIcon />;
      default: return <TruckIcon />;
    }
  };

  return (
    <>
      {/* Hero Section - Cleaner */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          color: brandColors.white,
          py: { xs: 10, md: 16 },
          backgroundImage: `linear-gradient(135deg, ${alpha(brandColors.black, 0.95)} 0%, ${alpha(brandColors.black, 0.85)} 100%), url(${truckImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: isMobile ? "scroll" : "fixed",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: "center" }}>
              <Chip
                label="OUR FLEET"
                sx={{
                  mb: 4,
                  backgroundColor: alpha(brandColors.green, 0.15),
                  color: brandColors.green,
                  border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                  px: 3,
                  py: 0.8,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  letterSpacing: 1.5,
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  color: brandColors.white,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                Modern & Well-Maintained Fleet
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: alpha(brandColors.white, 0.9),
                  fontWeight: 400,
                  fontStyle: 'italic',
                  maxWidth: 800,
                  mx: 'auto',
                }}
              >
                Fully maintained, clean trucks with C-Tracking systems
              </Typography>

              <Typography
                variant="subtitle1"
                sx={{
                  maxWidth: 800,
                  mx: "auto",
                  mb: 6,
                  color: alpha(brandColors.white, 0.8),
                  lineHeight: 1.7,
                  fontWeight: 300,
                  fontSize: "1.1rem",
                }}
              >
                Transco Logistics operates a fleet of {transcoTrucks.length} vehicles including 
                34 hose trailers, drop side trucks, specialized loaders, and service vehicles. 
                All equipped with C-tracking systems for security and safety management.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Chip
                  label={`${truckTypes.reduce((sum, type) => sum + type.count, 0)} Total Vehicles`}
                  sx={{
                    backgroundColor: alpha(brandColors.white, 0.1),
                    color: brandColors.white,
                    border: `1px solid ${alpha(brandColors.white, 0.2)}`,
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                  }}
                />
                <Chip
                  label="C-Tracking Systems"
                  icon={<GpsIcon />}
                  sx={{
                    backgroundColor: alpha(brandColors.green, 0.2),
                    color: brandColors.green,
                    border: `1px solid ${alpha(brandColors.green, 0.3)}`,
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                  }}
                />
                <Chip
                  label="Experienced Team"
                  icon={<GroupIcon />}
                  sx={{
                    backgroundColor: alpha(brandColors.orange, 0.2),
                    color: brandColors.orange,
                    border: `1px solid ${alpha(brandColors.orange, 0.3)}`,
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                  }}
                />
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ py: { xs: 8, md: 12 } }}>
        
        {/* Filter Section - Cleaner */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
            <Box>
              <Typography variant="h4" fontWeight={700} color={brandColors.black} gutterBottom>
                Browse Our Fleet
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Select a vehicle type to view specifications
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title="Clear filters">
                <IconButton
                  onClick={clearFilters}
                  sx={{
                    border: `1px solid ${alpha(brandColors.black, 0.15)}`,
                    borderRadius: "10px",
                    "&:hover": {
                      background: alpha(brandColors.orange, 0.05),
                      borderColor: brandColors.orange,
                    },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Chip
                label={`${filteredTrucks.length} vehicles`}
                size="medium"
                sx={{
                  bgcolor: alpha(brandColors.green, 0.08),
                  color: brandColors.green,
                  fontWeight: 600,
                  fontSize: "0.85rem",
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {truckTypes.map((type) => (
              <Button
                key={type.id}
                onClick={() => handleTypeChange(null, type.id)}
                variant={selectedType === type.id ? "contained" : "outlined"}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.5,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  borderColor: selectedType === type.id ? type.color : alpha(brandColors.black, 0.2),
                  backgroundColor: selectedType === type.id ? type.color : 'transparent',
                  color: selectedType === type.id ? brandColors.white : 'text.primary',
                  '&:hover': {
                    backgroundColor: selectedType === type.id ? type.color : alpha(type.color, 0.05),
                    borderColor: type.color,
                  },
                  position: 'relative',
                }}
              >
                {type.label}
                <Chip
                  label={type.count}
                  size="small"
                  sx={{
                    ml: 1.5,
                    height: 20,
                    minWidth: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    backgroundColor: selectedType === type.id ? alpha(brandColors.white, 0.2) : alpha(type.color, 0.1),
                    color: selectedType === type.id ? brandColors.white : type.color,
                  }}
                />
              </Button>
            ))}
          </Box>
        </Box>

        {/* Trucks Grid - Cleaner Design */}
        {filteredTrucks.length === 0 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <TruckIcon sx={{ fontSize: 80, color: alpha(brandColors.black, 0.1), mb: 3 }} />
            <Typography variant="h5" gutterBottom fontWeight={600} color={brandColors.black}>
              No vehicles found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: "auto" }}>
              No available vehicles in this category. Please try another filter.
            </Typography>
            <Button
              variant="outlined"
              onClick={clearFilters}
              sx={{
                borderRadius: "10px",
                px: 4,
                py: 1.2,
                fontWeight: 600,
                borderColor: brandColors.orange,
                color: brandColors.orange,
                "&:hover": {
                  background: alpha(brandColors.orange, 0.05),
                },
              }}
            >
              Show All Fleet
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredTrucks.map((truck, index) => (
              <Grid item xs={12} sm={6} lg={4} key={truck.id}>
                <Grow in timeout={(index + 1) * 200}>
                  <Card
                    sx={{
                      height: "100%",
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: "16px",
                      border: `1px solid ${alpha(brandColors.black, 0.08)}`,
                      background: brandColors.white,
                      transition: "all 0.3s ease",
                      boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                      "&:hover": {
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    {/* Quantity Badge */}
                    {truck.quantity > 1 && (
                      <Chip
                        label={`${truck.quantity} Units`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          zIndex: 1,
                          fontWeight: 600,
                          background: alpha(brandColors.green, 0.1),
                          color: brandColors.green,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 3, flexGrow: 1 }}>
                      {/* Truck Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "12px",
                            background: `linear-gradient(135deg, ${brandColors.orange} 0%, ${alpha(brandColors.orange, 0.8)} 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brandColors.white,
                            flexShrink: 0,
                          }}
                        >
                          {getTruckTypeIcon(truck.type)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={700} color={brandColors.black} gutterBottom>
                            {truck.name}
                          </Typography>
                          <Chip
                            label={truck.type}
                            size="small"
                            sx={{
                              bgcolor: alpha(brandColors.orange, 0.1),
                              color: brandColors.orange,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {truck.description}
                      </Typography>

                      {/* Specifications */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: 2, 
                        mb: 3,
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: brandColors.lightGray,
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                            Capacity
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {truck.capacity}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                            Length
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {truck.length}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                            Width
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {truck.width}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                            Height
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {truck.height}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Features */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} color={brandColors.black} gutterBottom>
                          Key Features
                        </Typography>
                        <Stack spacing={1}>
                          {truck.features.map((feature, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                              }}
                            >
                              <CheckIcon
                                sx={{
                                  fontSize: 16,
                                  color: brandColors.green,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2" color="text.primary">
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>

                      {/* Contact Info */}
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: "12px", 
                        backgroundColor: alpha(brandColors.green, 0.05),
                        border: `1px solid ${alpha(brandColors.green, 0.1)}`,
                      }}>
                        <Typography variant="subtitle2" fontWeight={600} color={brandColors.black} gutterBottom>
                          Get Pricing & Availability
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: brandColors.orange }} />
                          <Typography variant="body2" fontWeight={500}>
                            +268 7602 1597
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <EmailIcon sx={{ fontSize: 16, color: brandColors.green }} />
                          <Typography variant="body2" fontWeight={500}>
                            transcologistics26@gmail.com
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        component={Link}
                        to="/contact"
                        endIcon={<ArrowIcon />}
                        sx={{
                          py: 1.5,
                          borderRadius: "10px",
                          fontWeight: 600,
                          backgroundColor: brandColors.orange,
                          "&:hover": {
                            backgroundColor: alpha(brandColors.orange, 0.9),
                          },
                        }}
                      >
                        Request Quote
                      </Button>
                    </CardActions>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Call to Action - Cleaner */}
        <Box sx={{ mt: { xs: 10, md: 12 }, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} color={brandColors.black} gutterBottom>
            Need Custom Transport Solutions?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 700, mx: "auto", lineHeight: 1.7 }}>
            Serving clients across Eswatini with customized transportation solutions. 
            Our logistics experts can help you find the perfect solution for your needs.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={Link}
              to="/contact"
              startIcon={<PhoneIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                fontWeight: 600,
                backgroundColor: brandColors.orange,
                "&:hover": {
                  backgroundColor: alpha(brandColors.orange, 0.9),
                },
              }}
            >
              Contact Our Team
            </Button>
            <Button
              variant="outlined"
              component="a"
              href="tel:+26876021597"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "10px",
                fontWeight: 600,
                borderColor: brandColors.green,
                color: brandColors.green,
                "&:hover": {
                  backgroundColor: alpha(brandColors.green, 0.05),
                  borderColor: brandColors.green,
                },
              }}
            >
              Call Now
            </Button>
          </Stack>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ color: brandColors.green, fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                C-Tracking Security
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GpsIcon sx={{ color: brandColors.orange, fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                GPS Monitoring
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GroupIcon sx={{ color: brandColors.green, fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                Experienced Team
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Trucks;